import { useEffect, useState } from 'react';
import { gifts, type GiftBoxDto, type GiftItemDto, type ProgressNews } from '../net/api';
import type { AvatarLook } from '../art/avatar';
import { Sheet } from './Sheet';
import { Thumb } from './Thumb';
import { play } from '../audio/sound';

const slotOf = (type: string) => (['hat', 'top', 'shoes', 'accessory'].includes(type) ? type : null);
const thumb = (i: GiftItemDto) => ({ type: i.type, slot: slotOf(i.type), render: i.render });
const when = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

/** Gifts friends sent you (open them to put them in your backpack), and recent gifts both ways. */
export function GiftBox({ look, onClose, onChanged }: { look: AvatarLook; onClose: () => void; onChanged: (news?: ProgressNews) => void }) {
  const [box, setBox] = useState<GiftBoxDto>();
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string }>();
  const load = () => gifts.box().then(setBox, (e: Error) => setMsg({ kind: 'err', text: e.message }));
  useEffect(() => {
    void load();
  }, []);
  async function open(id: number) {
    try {
      const r = await gifts.open(id);
      play('gift');
      setMsg({ kind: 'ok', text: `You got ${r.quantity > 1 ? `${r.quantity} × ` : ''}${r.item} from ${r.from}! It's in your backpack.` });
      onChanged(r.progress);
      await load();
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message });
    }
  }
  return (
    <Sheet title="Gift box" subtitle="Presents from your friends. To send one, open Friends and pick “Send a gift”." onClose={onClose}>
      {msg && <p className={msg.kind === 'ok' ? 'note-ok' : 'form-error'} role="status">{msg.text}</p>}
      {box && !box.enabled && <p className="muted">Gifts are turned off for your account by your parent.</p>}
      {box?.waiting.length === 0 && <p className="muted">No new gifts right now.</p>}
      <div className="gift-grid">
        {box?.waiting.map((g) => (
          <article key={g.id} className="gift-card waiting">
            <span className="gift-wrap" aria-hidden="true">🎁</span>
            <strong>From {g.from}</strong>
            {g.note && <span className="small-print">“{g.note}”</span>}
            <button className="btn small" onClick={() => void open(g.id)}>Open</button>
          </article>
        ))}
      </div>
      {!!box?.history.length && (
        <section className="stack-sm my-reports">
          <h3 className="mini-head">Recent gifts</h3>
          <ul className="plain">
            {box.history.map((g) => (
              <li key={g.id} className="gift-line">
                <Thumb item={thumb(g.item)} look={look} size={40} />
                <span>
                  {g.direction === 'sent' ? `You gave ${g.to}` : `${g.from} gave you`} {g.quantity > 1 ? `${g.quantity} × ` : ''}
                  <strong>{g.item.name}</strong>
                  {g.direction === 'sent' && !g.openedAt && <span className="muted"> (not opened yet)</span>}
                  <span className="muted small-print"> · {when(g.createdAt)}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </Sheet>
  );
}

/** Pick something from your backpack to give a friend. */
export function SendGift({ to, look, onBack, onClose, onSent }: { to: string; look: AvatarLook; onBack: () => void; onClose: () => void; onSent: (news?: ProgressNews) => void }) {
  const [items, setItems] = useState<(GiftItemDto & { available: number })[]>();
  const [box, setBox] = useState<GiftBoxDto>();
  const [pick, setPick] = useState<string>();
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState('friend');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string }>();
  const load = () => {
    gifts.giftable().then((r) => setItems(r.items), (e: Error) => setMsg({ kind: 'err', text: e.message }));
    gifts.box().then(setBox, () => {});
  };
  useEffect(load, []);
  const chosen = items?.find((i) => i.key === pick);
  const stackable = !!chosen && ['fish', 'crop'].includes(chosen.type);
  const left = box ? box.dailyLimit - box.sentToday : 0;

  async function send() {
    if (!chosen) return;
    setBusy(true);
    setMsg(undefined);
    try {
      const r = await gifts.send({ to, item: chosen.key, quantity: stackable ? qty : 1, note });
      setMsg({ kind: 'ok', text: `Sent ${r.quantity > 1 ? `${r.quantity} × ` : ''}${r.item} to ${r.to}! (${r.dailyLimit - r.sentToday} gifts left today)` });
      setPick(undefined);
      setQty(1);
      onSent(r.progress);
      load();
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title={`Send a gift to ${to}`} subtitle={<button className="link-btn" onClick={onBack}>← Back to friends</button>} onClose={onClose} wide>
      {box && !box.enabled ? (
        <p className="muted">Gifts are turned off for your account by your parent.</p>
      ) : (
        <>
          <p className="muted small-print">
            The gift leaves your backpack and waits in {to}'s gift box. Gifts can't be taken back. {box && `You can send ${left} more today.`}
          </p>
          {items?.length === 0 && <p className="muted">You don't have anything you can give right now. Earned rewards and things placed in your cottage can't be given.</p>}
          <div className="gift-pick" role="listbox" aria-label="Choose a gift">
            {items?.map((i) => (
              <button key={i.key} role="option" aria-selected={pick === i.key} className={pick === i.key ? 'gift-option on' : 'gift-option'} onClick={() => { setPick(i.key); setQty(1); }}>
                <Thumb item={thumb(i)} look={look} size={64} />
                <span className="small-print">{i.name}</span>
                {i.available > 1 && <span className="muted small-print">×{i.available}</span>}
              </button>
            ))}
          </div>
          {chosen && (
            <div className="stack-sm gift-form">
              {stackable && chosen.available > 1 && (
                <label className="field">
                  <span>How many?</span>
                  <input type="number" min={1} max={Math.min(10, chosen.available)} value={qty} onChange={(e) => setQty(Math.max(1, Math.min(10, chosen.available, Number(e.target.value) || 1)))} />
                </label>
              )}
              <label className="field">
                <span>Add a note</span>
                <select value={note} onChange={(e) => setNote(e.target.value)}>
                  {box?.notes.map((n) => <option key={n.key} value={n.key}>{n.text}</option>)}
                </select>
              </label>
              <button className="btn wide" disabled={busy || left <= 0} onClick={() => void send()}>
                {busy ? 'Sending…' : `Give ${stackable && qty > 1 ? `${qty} × ` : ''}${chosen.name} to ${to}`}
              </button>
            </div>
          )}
        </>
      )}
      {msg && <p className={msg.kind === 'ok' ? 'note-ok' : 'form-error'} role="status">{msg.text}</p>}
    </Sheet>
  );
}
